'use client'

import { useEffect, useRef, useSyncExternalStore } from 'react'

// Overlays (sheets) that Telegram's BackButton should close before it leaves
// the screen. Telegram's onClick adds handlers rather than replacing them, so
// ScreenChrome keeps the only one and asks here first; the top entry wins.

const stack: { current: () => void }[] = []
const listeners = new Set<() => void>()
const emit = () => {
  for (const l of listeners) l()
}

/** Runs the top override; false when there is none. */
export function runBackOverride(): boolean {
  const top = stack[stack.length - 1]
  if (!top) return false
  top.current()
  return true
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function useHasBackOverride(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => stack.length > 0,
    () => false,
  )
}

/** While `active`, the BackButton calls `onBack` instead of navigating. */
export function useBackOverride(active: boolean, onBack: () => void): void {
  const ref = useRef(onBack)
  useEffect(() => {
    ref.current = onBack
  })
  useEffect(() => {
    if (!active) return
    const entry = { current: () => ref.current() }
    stack.push(entry)
    emit()
    return () => {
      const i = stack.indexOf(entry)
      if (i >= 0) stack.splice(i, 1)
      emit()
    }
  }, [active])
}
